import { 
  Directive, 
  ElementRef, 
  HostListener, 
  OnInit, 
  OnDestroy,
  Renderer2,
  Input,
  ComponentFactoryResolver,
  ViewContainerRef,
  ComponentRef,
  ApplicationRef,
  Injector,
  EmbeddedViewRef,
  Optional
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { SpellCheckService } from '../services/spell-check.service';
import { SpellCheckSuggestionComponent } from '../components/spell-check-suggestion/spell-check-suggestion.component';

@Directive({
    selector: '[appSpellCheck]',
    standalone: false
})
export class SpellCheckDirective implements OnInit, OnDestroy {
  @Input() enableSpellCheck: boolean = true;
  
  private suggestionComponentRef: ComponentRef<SpellCheckSuggestionComponent> | null = null;
  private isInputField: boolean = false;
  private originalSpellCheck: string | null = null;
  private clickListener: (() => void) | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private spellCheckService: SpellCheckService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    private appRef: ApplicationRef,
    private injector: Injector,
    @Optional() private ngControl: NgControl
  ) {}

  ngOnInit(): void {
    if (!this.enableSpellCheck) {
      return;
    }

    const element = this.el.nativeElement;
    this.isInputField = element.tagName === 'INPUT' || element.tagName === 'TEXTAREA';

    if (this.isInputField) {
      // Store original spellcheck attribute
      this.originalSpellCheck = element.getAttribute('spellcheck');
      
      // Enable native browser spellcheck with explicit language
      this.renderer.setAttribute(element, 'spellcheck', 'true');
      this.renderer.setAttribute(element, 'lang', 'en-US');
      
      // Add custom styling class
      this.renderer.addClass(element, 'spell-check-enabled');
      
      setTimeout(() => {
        const ctrl = this.ngControl?.control;
        const wasPristine = ctrl?.pristine;
        const wasUntouched = ctrl?.untouched;
        const inputEvent = new Event('input', { bubbles: true });
        element.dispatchEvent(inputEvent);
        if (ctrl) {
          if (wasPristine) {
            ctrl.markAsPristine();
          }
          if (wasUntouched) {
            ctrl.markAsUntouched();
          }
        }
      }, 100);
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this.enableSpellCheck || !this.isInputField) {
      return;
    }
    
    // Enable spellcheck when field is focused (open for editing)
    const element = this.el.nativeElement;
    this.renderer.setAttribute(element, 'spellcheck', 'true');
  }

  @HostListener('blur')
  onBlur(): void {
    // Close any open suggestion popover when field loses focus
    this.closeSuggestionPopover();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.enableSpellCheck || !this.isInputField) {
      return;
    }

    // Check if clicked on a misspelled word
    const element = this.el.nativeElement as HTMLInputElement | HTMLTextAreaElement;
    const cursorPosition = element.selectionStart || 0;
    const text = element.value;
    
    const wordData = this.getWordAtPosition(text, cursorPosition);
    
    if (wordData && this.spellCheckService.isMisspelled(wordData.word)) {
      this.showSuggestions(wordData.word, wordData.startIndex, wordData.endIndex, event);
    } else {
      this.closeSuggestionPopover();
    }
  }

  private getWordAtPosition(text: string, position: number): { word: string; startIndex: number; endIndex: number } | null {
    if (!text || position < 0 || position > text.length) {
      return null;
    }

    // Find word boundaries
    const wordRegex = /\b[\w']+\b/g;
    let match;
    
    while ((match = wordRegex.exec(text)) !== null) {
      const startIndex = match.index;
      const endIndex = startIndex + match[0].length;
      
      if (position >= startIndex && position <= endIndex) {
        return {
          word: match[0],
          startIndex,
          endIndex
        };
      }
    }
    
    return null;
  }

  private showSuggestions(word: string, startIndex: number, endIndex: number, event: MouseEvent): void {
    // Close existing popover
    this.closeSuggestionPopover();

    // Get suggestions from spell check service
    const suggestions = this.spellCheckService.getSuggestions(word);
    
    if (suggestions.length === 0) {
      return;
    }

    // Create suggestion component
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(SpellCheckSuggestionComponent);
    this.suggestionComponentRef = componentFactory.create(this.injector);
    
    // Set component inputs
    this.suggestionComponentRef.instance.suggestions = suggestions;
    this.suggestionComponentRef.instance.originalWord = word;
    
    // Subscribe to suggestion selection
    this.suggestionComponentRef.instance.suggestionSelected.subscribe((selectedWord: string) => {
      this.replaceWord(startIndex, endIndex, selectedWord);
      this.closeSuggestionPopover();
    });

    // Subscribe to close event
    this.suggestionComponentRef.instance.closed.subscribe(() => {
      this.closeSuggestionPopover();
    });

    // Attach component to DOM
    this.appRef.attachView(this.suggestionComponentRef.hostView);
    const domElem = (this.suggestionComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    // Position the popover near the cursor
    this.positionPopover(domElem, event);
  }

  private positionPopover(popoverElement: HTMLElement, event: MouseEvent): void {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Get popover dimensions (estimate if not yet rendered)
    const popoverHeight = popoverElement.offsetHeight || 300; // fallback estimate
    const popoverWidth = popoverElement.offsetWidth || 250;

    // Calculate initial position below the cursor
    let top = event.clientY + scrollTop + 10;
    let left = event.clientX + scrollLeft - 50;

    // Adjust if popover would go below viewport
    if (event.clientY + popoverHeight + 10 > viewportHeight) {
      // Position above the cursor instead
      top = event.clientY + scrollTop - popoverHeight - 10;
    }

    // Adjust if popover would go beyond right edge
    if (event.clientX + popoverWidth - 50 > viewportWidth) {
      left = viewportWidth - popoverWidth - 20 + scrollLeft;
    }

    // Adjust if popover would go beyond left edge
    if (left < scrollLeft + 10) {
      left = scrollLeft + 10;
    }

    popoverElement.style.position = 'fixed';
    popoverElement.style.top = `${top - scrollTop}px`;
    popoverElement.style.left = `${left - scrollLeft}px`;
    popoverElement.style.zIndex = '99999';
  }

  private replaceWord(startIndex: number, endIndex: number, newWord: string): void {
    const element = this.el.nativeElement as HTMLInputElement | HTMLTextAreaElement;
    const text = element.value;
    const newText = text.substring(0, startIndex) + newWord + text.substring(endIndex);
    
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      // Update the element value
      element.value = newText;
      
      // If using reactive forms, update the form control value
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(newText, { emitEvent: true });
        this.ngControl.control.markAsDirty();
        this.ngControl.control.markAsTouched();
      }
      
      // Trigger input event to ensure change detection
      const inputEvent = new Event('input', { bubbles: true });
      element.dispatchEvent(inputEvent);
      
      // Set cursor position after the replaced word
      const newCursorPosition = startIndex + newWord.length;
      element.setSelectionRange(newCursorPosition, newCursorPosition);
      element.focus();
    }, 0);
  }

  private closeSuggestionPopover(): void {
    if (this.suggestionComponentRef) {
      this.appRef.detachView(this.suggestionComponentRef.hostView);
      this.suggestionComponentRef.destroy();
      this.suggestionComponentRef = null;
    }
  }

  ngOnDestroy(): void {
    this.closeSuggestionPopover();
    
    // Restore original spellcheck attribute
    if (this.isInputField && this.originalSpellCheck !== null) {
      this.renderer.setAttribute(this.el.nativeElement, 'spellcheck', this.originalSpellCheck);
    }
    
    // Remove custom styling class
    this.renderer.removeClass(this.el.nativeElement, 'spell-check-enabled');
  }
}
